import { useMemo } from "react";

import { generateBidsSearchUrl } from "components/bids/utils";
import toast from "components/ui/toast/Toast";
import { useBidWorkflow } from "lib/hooks/api/bid/bids/useBidWorkflow";
import { useCreateBidWorkflow } from "lib/hooks/api/bid/bids/useCreateBidWorkflow";
import { useUpdateBidWorkflow } from "lib/hooks/api/bid/bids/useUpdateBidWorkflow";
import { Content, Page } from "./shared";
import { CustomiseWorkflowInput, mapBidWorkflowToWorkflow, toCreateTransition } from "./utils";
import WorkflowContainer from "./WorkflowContainer";

export default function BidWorkflow(): JSX.Element {
  const { data, isLoading, isError } = useBidWorkflow({ withCounts: true });

  const { mutate: updateBidWorkflow } = useUpdateBidWorkflow({
    onSuccess: () => {
      toast.success("Bid workflow updated successfully");
    },
    onError: () => toast.error("Failed to update bid workflow"),
  });

  const { mutate: createBidWorkflow } = useCreateBidWorkflow({
    onSuccess: () => {
      toast.success("Bid workflow updated successfully");
    },
    onError: () => toast.error("Failed to update bid workflow"),
  });

  const workflow = useMemo(() => (data ? mapBidWorkflowToWorkflow(data) : undefined), [data]);

  const handleWorkflowChange = (input: CustomiseWorkflowInput) => {
    if (input.isDefault) {
      createBidWorkflow({
        createWorkflow: {
          transitions: input.transitions.map(toCreateTransition),
          stageReplacements: input.stageReplacements ?? [],
        },
        stageFilterRequest: {},
      });
    } else {
      updateBidWorkflow({
        updateWorkflow: {
          id: input.workflowId,
          transitions: input.transitions,
          // No replacements are needed because IDs remain unchanged when updating workflows
          stageReplacements: [],
        },
        stageFilterRequest: {},
      });
    }
  };

  return (
    <Page>
      <Content>
        <WorkflowContainer
          workflow={workflow}
          onWorkflowChange={handleWorkflowChange}
          isError={isError}
          isLoading={isLoading}
          title="Customise your Bid workflow"
          workflowType="bid"
          generateSearchUrl={(stageId) => generateBidsSearchUrl({ stageIds: [stageId] })}
        />
      </Content>
    </Page>
  );
}
